import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave, fnGetTableById } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global, globalMenu } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnTree from "@shared/components/GridV2/SnTree";
import SnButton from "@shared/components/SnButton";
import SnResizeBar from "@shared/components/SnResizeBar";
import {
  CellValueChangedEvent,
  ColumnConfig,
  RowData,
  SnGridRef,
} from "@shared/components/GridV2/SnGridTypes";
import { ColDef } from "ag-grid-community";
import { v4 as uuid } from "uuid";
import { menuInfo } from "@shared/type/CommonType";
import { showPopupModal } from "@widgets/CommonModal";
import SN_PRD_BAS_REG_044_POP_001 from "./SN_PRD_BAS_REG_044_POP_001";

interface Props {
  Menuprops: menuInfo;
}

type CdOpOption = { code: string; name: string; [key: string]: unknown };

// 한글 주석: 조회 결과 셋 순서 (sn_prd_bas_reg_044_01)
const DS_BOMH = 0; // 트리에 포함된 모품목들의 prd_bomh 헤더
const DS_BOM = 1; // 품목 전개(재귀) + 라인 상세. 트리와 grid2가 함께 사용
const DS_CD_OP = 2; // 공정 콤보

const SN_PRD_BAS_REG_044 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();
  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    tree: React.createRef<any>(),
    grid2: React.createRef<SnGridRef>(),
    sheet1: React.createRef<SnGridRef>(),
  });
  const copyRow = useRef<RowData | null>(null);
  const isBulkUpdating = useRef(false);
  // 한글 주석: 현재 선택된 트리 노드. 행추가/전체삭제/헤더 처리의 모품목 기준.
  const focusedTreeRowRef = useRef<RowData | null>(null);
  // 한글 주석: grid2에 걸린 node 필터 집합. 비어 있으면 필터 없음(루트).
  const nodeFilterRef = useRef<string[]>([]);
  // 한글 주석: 조회 직전 grid2에 있던 원본 행(모품목별 기존 라인 유무 판단용).
  const originalRowsRef = useRef<RowData[]>([]);

  const today = new Date();
  const todayform = `${today.getFullYear()}${(today.getMonth() + 1)
    .toString()
    .padStart(2, "0")}${today.getDate().toString().padStart(2, "0")}`;
  const [formData, setFormData] = useState<RowData>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColumns] = useState<{
    tree: ColumnConfig[];
    grid2: ColumnConfig[];
    sheet1: ColumnConfig[];
  }>({
    tree: [],
    grid2: [],
    sheet1: [],
  });
  const [dataSources, setDataSources] = useState<{
    bomh: RowData[];
    tree: RowData[];
    grid2: RowData[];
    sheet1: RowData[];
  }>({
    bomh: [],
    tree: [],
    grid2: [],
    sheet1: [],
  });
  const [cdOpList, setCdOpList] = useState<CdOpOption[]>([]);
  const [isTreeExpanded, setIsTreeExpanded] = useState(true);

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const getErrorMessage = (error: unknown, fallback: string) => {
    if (error instanceof Error) return error.message;
    if (typeof error === "string") return error;
    return fallback;
  };

  const isRootRow = (row?: RowData | null) =>
    !!row && Number(row.no_level ?? 0) === 0;

  // 한글 주석: 선택 트리 노드가 가리키는 모품목 정보. 노드가 없으면 조회조건 품목.
  const getCurrentParent = () => {
    const form = searchRef.current?.getFormdata?.() ?? {};
    const node = focusedTreeRowRef.current;
    if (!node) {
      return {
        cd_biz: form.cd_biz,
        cd_item: form.cd_item ?? "",
        nm_item: form.nm_item ?? "",
        dc_item_spec: form.dc_item_spec ?? "",
        dc_item_unit: form.dc_item_unit ?? "",
        no_level: 0,
        node: form.cd_item ?? "",
        path: form.cd_item ?? "",
        dc_rmk: form.dc_rmk,
      };
    }
    return {
      cd_biz: node.cd_biz ?? form.cd_biz,
      cd_item: node.cd_matl ?? "",
      nm_item: node.nm_matl ?? "",
      dc_item_spec: node.dc_item_spec_matl ?? "",
      dc_item_unit: node.dc_item_unit_matl ?? "",
      no_level: Number(node.no_level ?? 0),
      node: String(node.node ?? ""),
      path: String(node.path ?? ""),
      dc_rmk: form.dc_rmk,
    };
  };

  // 한글 주석: GridV2는 setComboData가 없으므로 cd_op 콤보를 컬럼 설정(js_combo)으로 주입한다.
  const updateCdOpList = (nextCdOpList: CdOpOption[]) => {
    setCdOpList(nextCdOpList);
    setColumns((prev) => ({
      ...prev,
      grid2: prev.grid2.map((column) =>
        column.cd_column === "cd_op"
          ? { ...column, js_combo: JSON.stringify(nextCdOpList) }
          : column
      ),
    }));
  };

  // 한글 주석: grid2에 0레벨(모품목) 행을 함께 보여주되 편집은 막는다.
  // dc_editcon은 "참이면 편집 불가" 조건식이므로 no_level == 0 을 OR로 덧붙인다.
  const lockRootRowColumns = (cols: ColumnConfig[]) =>
    cols.map((col) => {
      const cond = String((col as any).dc_editcon ?? "").trim();
      return {
        ...col,
        dc_editcon: cond ? `(${cond}) || no_level == 0` : "no_level == 0",
      };
    });

  const convertToTreeData = (rows: RowData[]) =>
    rows.map((row) => ({
      ...row,
      node: String(row.node ?? row.path ?? row.cd_matl),
      node_p: row.node_p ?? null,
      cd_matl_display:
        row.cd_matl_display ?? `${row.cd_matl} [${row.nm_matl ?? ""}]`,
      sort_key: row.sort_key !== undefined ? Number(row.sort_key) : 0,
    }));

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);
        setColumns({
          tree: rtn.grid.grid1 ?? [],
          grid2: lockRootRowColumns(rtn.grid.grid2 ?? []),
          sheet1: rtn.grid.sheet1 ?? [],
        });
      } catch (error: unknown) {
        await showDialog(
          "error",
          getErrorMessage(error, "초기 화면 정보를 불러오지 못했습니다.")
        );
      }
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onDelete,
    onAddRow,
    onSave,
    onExcelUpload,
    onExcelDownLoad,
  }));

  // 한글 주석: 조회/저장 후 재조회 공통. 선택 노드는 건드리지 않는다(onRowDataReady가 복원).
  const loadBom = async () => {
    const grid = getGridCurrent("grid2");
    const data = await fnGetDataSet({
      id: "sn_prd_bas_reg_044_01",
      params: searchRef.current.getFormdata(),
    });
    const bomRows = convertToTreeData(data[DS_BOM] ?? []);
    // 한글 주석: grid2에도 0레벨(모품목) 행을 함께 보여준다. 단, 0레벨은 라인이 아니므로
    // 편집·삭제·저장 대상에서 제외한다(lockRootRowColumns, onDeleteRow, onSave 참고).
    originalRowsRef.current = bomRows.filter((row) => !isRootRow(row));
    updateCdOpList(data[DS_CD_OP] ?? []);
    setDataSources((prev) => ({
      ...prev,
      bomh: data[DS_BOMH] ?? [],
      tree: bomRows,
      grid2: bomRows,
    }));
    grid.resetUpdate();
    copyRow.current = null;
  };

  const onSearch = async () => {
    const grid = getGridCurrent("grid2");
    const modifiedRows = grid.getChange();
    if (modifiedRows.modifiedRowcount > 0) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        // 한글 주석: 저장 후 재조회는 아래에서 한 번만 한다(중복 조회 방지).
        const saved = await onSave(false);
        if (!saved) return false;
      }
    }

    try {
      // 한글 주석: 일반 조회는 선택 노드·필터를 초기화하고 루트부터 다시 시작한다.
      focusedTreeRowRef.current = null;
      nodeFilterRef.current = [];
      gridRefs.current.tree.current?.clearFocus?.();
      grid.clearFocus?.();
      grid.resetFilters?.();
      await loadBom();
      return true;
    } catch (error: unknown) {
      await showDialog(
        "error",
        getErrorMessage(error, "조회 중 오류가 발생했습니다.")
      );
      return false;
    }
  };

  // 한글 주석: grid2 현재 행 기준으로 roots 이하 자손 node 집합을 만든다(신규 임시행 포함).
  const collectDescendantNodes = (roots: string[]) => {
    const grid = getGridCurrent("grid2");
    const byParent = new Map<string, string[]>();
    grid.forEachNode((rowNode) => {
      const row = rowNode.data;
      const node = row?.node;
      const nodeP = row?.node_p;
      if (!node || !nodeP) return;
      if (!byParent.has(nodeP)) byParent.set(nodeP, []);
      byParent.get(nodeP)!.push(String(node));
    });

    const result = new Set<string>();
    const stack = [...roots];
    while (stack.length > 0) {
      const current = stack.pop() as string;
      if (result.has(current)) continue;
      result.add(current);
      (byParent.get(current) || []).forEach((child) => stack.push(child));
    }
    return Array.from(result);
  };

  const applyNodeFilter = (row: RowData | null) => {
    const grid = getGridCurrent("grid2");
    if (!row || isRootRow(row)) {
      nodeFilterRef.current = [];
      grid.resetFilters();
      return;
    }
    const nodes = collectDescendantNodes([String(row.node)]);
    nodeFilterRef.current = nodes;
    grid.applyFilters({ node: nodes });
  };

  const onFocusedRowChanged = (row: RowData | null) => {
    if (!row) return;
    focusedTreeRowRef.current = row;
    applyNodeFilter(row);
  };

  const onRowDataReady = () => {
    const treeApi = gridRefs.current.tree.current;
    if (!treeApi) return;

    requestAnimationFrame(() => {
      const keepNode = focusedTreeRowRef.current?.node;
      const keepRowNode = keepNode ? treeApi.getRowNode?.(keepNode) : null;
      if (keepRowNode && keepRowNode.rowIndex != null) {
        treeApi.selectAndFocusRow?.(keepRowNode.rowIndex);
        // 한글 주석: 같은 노드 재선택은 포커스 이벤트가 안 올 수 있어 필터를 직접 다시 건다.
        applyNodeFilter(keepRowNode.data);
        return;
      }
      treeApi.selectAndFocusRow?.(0);
    });
  };

  const onTreeToggleExpand = () => {
    const treeApi = gridRefs.current.tree.current;
    if (!treeApi) return;
    if (isTreeExpanded) {
      treeApi.forEachNode((node: any) =>
        node.setExpanded(Number(node.data?.no_level ?? 0) === 0)
      );
    } else {
      treeApi.setExpandedAll(true);
    }
    setIsTreeExpanded(!isTreeExpanded);
  };

  const autoGroupColumnDef = useMemo<ColDef>(
    () => ({
      headerName: "품목",
      field: "cd_matl_display",
      flex: 1,
      minWidth: 260,
      editable: false,
      cellRendererParams: {
        suppressCount: true,
        suppressDoubleClickExpand: true,
        suppressHoverHighlight: false,
      },
    }),
    []
  );

  const getRowId = useCallback(
    (params: any) => String(params.data.node ?? params.data.cd_matl),
    []
  );

  const onExcelUpload = async () => {
    try {
      if (searchRef.current.getFormdata().cd_item.trim() === "") {
        await showDialog("alter", "품목을 선택하시기 바랍니다");
        return false;
      }

      const component = (
        <SN_PRD_BAS_REG_044_POP_001
          Menuprops={Menuprops}
          PkFields={["cd_item, no_line"]}
          tableName="prd_boml"
          controllerName="SN_PRD_BAS_REG_004_SAVE_01"
          cdOpList={cdOpList}
          cd_biz={searchRef.current.getFormdata().cd_biz}
          userInfo={userInfo}
        />
      );

      const result = await showPopupModal({
        title: "엑셀 업로드",
        children: component,
        width: "80%",
        height: "90VH",
        okayBtnText: "저장",
      });
      if (result) await onSearch();
      return true;
    } catch (error: unknown) {
      await showDialog("error", getErrorMessage(error, "Excel 업로드 오류"));
      return false;
    }
  };

  const onExcelDownLoad = async () => {
    try {
      if (searchRef.current.getFormdata().cd_item.trim() === "") {
        await showDialog("alter", "품목을 선택하시기 바랍니다");
        return false;
      }

      const param = {
        id: "sn_prd_bas_reg_044_02",
        params: searchRef.current.getFormdata(),
      };

      setDataSources((prev) => ({ ...prev, sheet1: [] }));
      const data = await fnGetDataSet(param);
      setDataSources((prev) => ({ ...prev, sheet1: data[0] ?? [] }));

      // 한글 주석: 숨김 그리드에 데이터가 바인딩될 때까지 잠시 대기한다.
      await new Promise((resolve) => setTimeout(resolve, 500));
      const sheetGrid = getGridCurrent("sheet1");
      const displayedColumns = sheetGrid.getAllDisplayedColumns() || [];
      const columnKeys = displayedColumns.map((column) => column.getColId());

      sheetGrid.exportDataAsExcel({
        columnKeys,
        skipPinnedBottom: true,
        fileName: `${globalMenu.getState().activeMenuInfo.nm_menu}.xlsx`,
        sheetName: "Sheet1",
      });
      return true;
    } catch (error: unknown) {
      await showDialog("error", getErrorMessage(error, "Excel 다운로드 오류"));
      return false;
    }
  };

  // 한글 주석: 기존 행은 path에 자품목까지 포함되고, 신규 임시행은 모품목 경로만 들어 있다.
  const getFullPath = (row: RowData) => {
    const path = String(row.path ?? "");
    if (row.state === "insert") {
      return row.cd_matl ? `${path}>${row.cd_matl}` : path;
    }
    return path;
  };

  const getParentPath = (row: RowData) => {
    const path = String(row.path ?? "");
    if (row.state === "insert") return path;
    const idx = path.lastIndexOf(">");
    return idx >= 0 ? path.substring(0, idx) : path;
  };

  // 한글 주석: 행추가의 모품목 기준을 정한다.
  // child   → 선택 행의 자품목이 모품목(레벨+1)
  // sibling → 선택 행과 같은 모품목(같은 레벨)
  // 선택 행이 없으면 선택된 트리 노드가 모품목
  const parentFrom = (base: RowData | null, kind: "sibling" | "child") => {
    if (!base) return getCurrentParent();

    const form = searchRef.current?.getFormdata?.() ?? {};
    if (kind === "child") {
      return {
        cd_biz: base.cd_biz ?? form.cd_biz,
        cd_item: String(base.cd_matl ?? ""),
        nm_item: base.nm_matl ?? "",
        dc_item_spec: base.dc_item_spec_matl ?? "",
        dc_item_unit: base.dc_item_unit_matl ?? "",
        no_level: Number(base.no_level ?? 0),
        node: String(base.node ?? ""),
        path: getFullPath(base),
        dc_rmk: form.dc_rmk,
      };
    }
    return {
      cd_biz: base.cd_biz ?? form.cd_biz,
      cd_item: String(base.cd_item ?? ""),
      nm_item: base.nm_item ?? "",
      dc_item_spec: base.dc_item_spec ?? "",
      dc_item_unit: base.dc_item_unit ?? "",
      no_level: Math.max(Number(base.no_level ?? 1) - 1, 0),
      node: String(base.node_p ?? ""),
      path: getParentPath(base),
      dc_rmk: form.dc_rmk,
    };
  };

  // 한글 주석: 새 행을 "모품목(parent)에 속한 하위 묶음의 맨 끝"에 끼워 넣기 위한 삽입 위치.
  // 예) …1 행에서 행추가 → 마지막 …1(과 그 하위) 뒤, …2 행에서 행추가 → 같은 …1에 속한 마지막 …2(와 그 하위) 뒤.
  // addRow의 addIndex는 화면 표시 순서가 아니라 원본 데이터 순서 기준이므로(필터와 무관) forEachNode로 센다.
  // 데이터는 path 순(재귀 전개 순)이라 모품목의 서브트리는 연속 구간이다. 모품목을 못 찾으면 맨 끝.
  const insertIndexAtSubtreeEnd = (
    parentNode: string | undefined | null
  ): number | undefined => {
    const key = String(parentNode ?? "");
    if (!key) return undefined;
    const subtree = new Set(collectDescendantNodes([key]));
    let last: number | undefined;
    let idx = 0;
    getGridCurrent("grid2").forEachNode((node) => {
      if (subtree.has(String(node.data?.node ?? ""))) last = idx;
      idx++;
    });
    return last === undefined ? undefined : last + 1;
  };

  const startAddRow = async (kind: "sibling" | "child") => {
    const grid = getGridCurrent("grid2");
    const base: RowData | null =
      grid.getSelectedRows()[0] ?? grid.focusedRowNode()?.data ?? null;

    if (kind === "child") {
      if (!base) {
        await showDialog("alter", "하위추가할 행을 선택해 주세요.");
        return false;
      }
      if (String(base.cd_matl ?? "").trim() === "") {
        await showDialog(
          "alter",
          "자품목이 입력된 행에서만 하위추가할 수 있습니다."
        );
        return false;
      }
    }

    // 한글 주석: 0레벨(모품목) 행에서 행추가하면 같은 레벨이 없으므로 하위추가로 처리한다.
    const parent = parentFrom(base, base && isRootRow(base) ? "child" : kind);
    try {
      if ((parent.cd_item ?? "").trim() === "") {
        await showDialog("alter", "품목을 선택하시기 바랍니다");
        return false;
      }

      const payload = {
        id: "item_rev",
        params: {
          cd_firm: userInfo.cd_firm,
          cd_item: parent.cd_item,
        },
      };

      const data = await fnGetTableById(payload);
      if (Array.isArray(data) && data.length > 0) {
        await showDialog("alter", "개정관리되는 품목입니다.");
        return false;
      }

      // 한글 주석: 행을 여기서 완성해 넣으므로 onBeforeAddRow 훅은 건너뛴다(useEvent=false).
      // 모품목에 속한 하위 묶음의 맨 끝에 삽입한다.
      grid.addRow(
        buildNewRow(parent),
        false,
        false,
        insertIndexAtSubtreeEnd(parent.node)
      );
      refreshNodeFilter();
      return true;
    } catch (error: unknown) {
      await showDialog(
        "error",
        getErrorMessage(error, "행 추가 중 오류가 발생했습니다.")
      );
      return false;
    }
  };

  // 한글 주석: 필터가 걸린 상태면 새 행까지 포함해 다시 계산한다.
  const refreshNodeFilter = () => {
    if (nodeFilterRef.current.length > 0) {
      applyNodeFilter(focusedTreeRowRef.current);
    }
  };

  // 한글 주석: 그리드 기본 행추가 버튼 = 선택 행과 같은 레벨에 추가
  const onAddRow = () => startAddRow("sibling");
  // 한글 주석: 헤더의 하위추가 버튼 = 선택 행의 자품목 아래(레벨+1)에 추가
  const onAddChildRow = () => startAddRow("child");

  const onCellValueChanged = async (event: CellValueChangedEvent) => {
    try {
      if (!event || isBulkUpdating.current) return;

      // 한글 주석: 0레벨(모품목) 행은 편집 불가. 어떤 경로로든 값이 바뀌면 되돌린다.
      if (isRootRow(event.rowData)) {
        event.rowNode.setDataValue(event.colId, event.oldValue, "noEvent");
        return;
      }

      if (event.colId === "rt_loss" || event.colId === "qt_just") {
        calculateAmounts(event.rowNode, event.rowData);
      }

      if (event.colId === "cd_matl") {
        await validateMatl(event);
      }

      if (event.colId === "dt_start" || event.colId === "dt_end") {
        const start = Number(event.rowData?.dt_start);
        const end = Number(event.rowData?.dt_end);

        if (start > end) {
          await showDialog(
            "alter",
            "시작일 보다 종료일이 더 빠릅니다.\r\n날짜를 확인해주세요."
          );
          getGridCurrent("grid2").stopEditing();
          event.rowNode.setDataValue(event.colId, event.oldValue, "noEvent");
          return;
        }
      }

      if (event.colId === "cd_op") {
        await applyCdOpToAllRows(event);
      }
    } catch (error: unknown) {
      await showDialog(
        "error",
        getErrorMessage(error, "그리드 변경 처리 중 오류가 발생했습니다.")
      );
    }
  };

  // 한글 주석: 행마다 모품목이 다르므로 변경된 행만 검사한다. 모품목=자품목 금지, 상위 경로 순환 금지.
  const validateMatl = async (event: CellValueChangedEvent) => {
    const row = event.rowData;
    const matl = String(row?.cd_matl ?? "");
    if (!matl) return;

    const path = String(row?.path ?? "");
    const onPath = path.split(">").includes(matl);
    if (matl === row?.cd_item || onPath) {
      await showDialog(
        "alter",
        matl === row?.cd_item
          ? "모품목과 동일한 품목을 자품목으로 등록할 수 없습니다."
          : "상위 경로에 있는 품목은 자품목으로 등록할 수 없습니다."
      );
      event.rowNode.setDataValue("cd_matl", "", "noEvent");
      event.rowNode.setDataValue("nm_matl", "", "noEvent");
      event.rowNode.setDataValue("dc_item_spec_matl", "", "noEvent");
      event.rowNode.setDataValue("dc_item_unit_matl", "", "noEvent");
      event.rowNode.setSelected(true);
      getGridCurrent("grid2").setFocusedCell(event.rowIndex ?? 0, "cd_matl");
    }
  };

  // 한글 주석: 공정 일괄 적용은 현재 화면에 보이는(필터된) 행에만 적용한다.
  const applyCdOpToAllRows = async (event: CellValueChangedEvent) => {
    if (event.rowData?.yn_condition === "Y") {
      event.rowNode.setDataValue("yn_condition", "N", "noEvent");
      return;
    }

    const ok = await showDialog(
      "confirm",
      "해당 공정으로 일괄 적용 하시겠습니까?"
    );
    if (!ok) return;

    const grid = getGridCurrent("grid2");
    isBulkUpdating.current = true;
    try {
      grid.forEachFilteredNode((node) => {
        node.setDataValue("yn_condition", "Y", "noEvent");
        node.setDataValue("cd_op", event.newValue, "noEvent");
        node.setDataValue("yn_condition", "N", "noEvent");
      });
    } finally {
      isBulkUpdating.current = false;
    }
  };

  const handleContextMenu = async (params: { id?: string }) => {
    const grid = getGridCurrent("grid2");
    const selectedRow =
      grid.getSelectedRows()[0] ?? grid.focusedRowNode()?.data;
    if (!selectedRow) return;

    switch (params.id) {
      case "fn_rowcopy_ms":
        copyRow.current = { ...selectedRow };
        break;
      case "fn_rowpaste_ms":
        if (!copyRow.current) {
          await showDialog("alter", "품목을 선택하시기 바랍니다.");
          return;
        }
        const pasteParent = getCurrentParent();
        grid.addRow(
          buildNewRow(pasteParent, copyRow.current),
          false,
          false,
          insertIndexAtSubtreeEnd(pasteParent.node)
        );
        refreshNodeFilter();
        break;
    }
  };

  const calculateAmounts = (
    rowNode: CellValueChangedEvent["rowNode"],
    row: RowData
  ) => {
    const qtJust = Number(row.qt_just ?? 0);
    const rtLoss = Number(row.rt_loss ?? 0);
    const qtNeed = qtJust + qtJust * rtLoss * 0.01;
    rowNode.setDataValue("qt_need", qtNeed, "noEvent");
  };

  // 한글 주석: 모품목 정보로 신규 라인 행을 만든다. source가 있으면(붙여넣기) 그 값을 바탕으로 한다.
  const buildNewRow = (
    parent: ReturnType<typeof getCurrentParent>,
    source?: RowData | null
  ) => {
    let maxNoLine = 0;
    getGridCurrent("grid2").forEachNode((node) => {
      if (node.data?.cd_item !== parent.cd_item) return;
      maxNoLine = Math.max(maxNoLine, Number(node.data.no_line || 0));
    });

    const noLevel = parent.no_level + 1;
    const { KeyField, state, block, ...pasted } = source ?? {};
    return {
      cd_firm: userInfo.cd_firm,
      tp_bom: "STD",
      qt_just: 1,
      qt_avg: 0,
      rt_loss: 0,
      qt_need: 1,
      qt_standard: 0,
      dt_start: todayform,
      dt_end: "21001231",
      yn_condition: "N",
      cd_user_reg: userInfo.cd_user,
      ...pasted,
      cd_biz: parent.cd_biz,
      cd_item: parent.cd_item,
      nm_item: parent.nm_item,
      dc_item_spec: parent.dc_item_spec,
      dc_item_unit: parent.dc_item_unit,
      no_line: maxNoLine + 1,
      no_level: noLevel,
      indent: `${"…".repeat(noLevel)}${noLevel}`,
      path: parent.path,
      node: uuid(),
      node_p: parent.node,
      sort_key: maxNoLine + 1,
    };
  };

  // 한글 주석: 화면 밖(챗봇 등)에서 addRow()를 부르는 경로용 폴백. 선택 트리 노드 아래에 추가.
  const onBeforeAddRow = () => buildNewRow(getCurrentParent());

  // 한글 주석: 선택 노드 품목의 직계 라인만 전부 삭제한다(하위 품목의 BOM은 유지).
  const onDelete = async () => {
    const grid = getGridCurrent("grid2");
    const parent = getCurrentParent();
    const targets: CellValueChangedEvent["rowNode"][] = [];
    grid.forEachNode((node) => {
      if (isRootRow(node.data)) return;
      if (node.data?.cd_item === parent.cd_item) targets.push(node);
    });
    if (targets.length < 1) return false;

    const confirm = await showDialog(
      "confirm",
      `[${parent.cd_item}] 품목의 BOM 전체 내역이 삭제됩니다. 삭제하시겠습니까?`
    );
    if (!confirm) return false;

    targets.forEach((node) => grid.deleteRowByNode(node));
    return await onSave();
  };

  // 한글 주석: 선택 행을 삭제한다. 선택 행의 자품목이 하위자재를 가진 반제품이면
  // 하위 라인들도 화면에서 함께 사라지게 한다. 단, 하위 라인은 그 반제품 자체의 BOM이라
  // DB 삭제 대상이 아니므로 변경 추적 없이 UI에서만 제거한다(저장 후 재조회 시 자연히 빠짐).
  const onDeleteRow = async () => {
    try {
      const grid = getGridCurrent("grid2");
      const selected = grid.getSelectedRows();
      if (selected.length === 0) {
        await showDialog("alter", "삭제할 행을 선택해 주세요.");
        return false;
      }
      if (selected.some((row) => isRootRow(row))) {
        await showDialog("alter", "0레벨(모품목) 행은 삭제할 수 없습니다.");
        return false;
      }

      const rootNodes = selected
        .map((row) => String(row.node ?? ""))
        .filter(Boolean);
      const rootSet = new Set(rootNodes);
      const descendants = collectDescendantNodes(rootNodes).filter(
        (node) => !rootSet.has(node)
      );

      if (descendants.length > 0) {
        const ok = await showDialog(
          "confirm",
          "하위자재가 있는 반제품입니다. 하위 내역도 화면에서 함께 제외됩니다. 삭제하시겠습니까?"
        );
        if (!ok) return false;
      }

      // 1) 선택 행: 실제 삭제(저장 시 prd_boml delete 전송)
      grid.deleteSelectedRows();

      // 2) 하위 행: 신규 임시행은 추적 목록에서 빼고, 기존 행은 UI에서만 제거
      const descSet = new Set(descendants);
      const newRowNodes: CellValueChangedEvent["rowNode"][] = [];
      const uiOnlyRows: RowData[] = [];
      grid.forEachNode((node) => {
        const key = String(node.data?.node ?? "");
        if (!descSet.has(key)) return;
        if (node.data?.state === "insert") newRowNodes.push(node);
        else uiOnlyRows.push(node.data);
      });
      newRowNodes.forEach((node) => grid.deleteRowByNode(node));
      if (uiOnlyRows.length > 0) {
        grid.applyTransaction({ remove: uiOnlyRows });
      }
      return true;
    } catch (error: unknown) {
      await showDialog(
        "error",
        getErrorMessage(error, "행 삭제 중 오류가 발생했습니다.")
      );
      return false;
    }
  };

  // 한글 주석: 변경 라인들의 모품목별로 prd_bomh insert/update/delete를 결정한다.
  const buildBomhSaveData = (changesRow: {
    insert?: RowData[];
    update?: RowData[];
    delete?: RowData[];
  }) => {
    const grid = getGridCurrent("grid2");
    const headerItems = new Set(
      (dataSources.bomh || []).map((h) => String(h.cd_item))
    );
    const remainingByItem = new Map<string, number>();
    grid.forEachNode((node) => {
      if (isRootRow(node.data)) return; // 0레벨 행은 라인이 아니다
      const item = String(node.data?.cd_item ?? "");
      remainingByItem.set(item, (remainingByItem.get(item) ?? 0) + 1);
    });
    const originalByItem = new Set(
      originalRowsRef.current.map((row) => String(row.cd_item))
    );

    const insertItems = new Set<string>();
    const updateItems = new Set<string>();
    const deleteItems = new Set<string>();

    (changesRow.insert ?? []).forEach((row) => {
      const item = String(row.cd_item);
      if (headerItems.has(item)) {
        updateItems.add(item);
      } else if (!originalByItem.has(item)) {
        // 한글 주석: 헤더도 기존 라인도 없는 품목만 헤더를 새로 만든다.
        // 기존 라인이 있는데 헤더만 없는 품목에 insert를 보내면 백엔드가 기존 라인을 전부 지운다.
        insertItems.add(item);
      }
    });
    (changesRow.update ?? []).forEach((row) => {
      const item = String(row.cd_item);
      if (headerItems.has(item)) updateItems.add(item);
    });
    (changesRow.delete ?? []).forEach((row) => {
      const item = String(row.cd_item);
      if ((remainingByItem.get(item) ?? 0) === 0 && headerItems.has(item)) {
        deleteItems.add(item);
      } else if (headerItems.has(item)) {
        updateItems.add(item);
      }
    });
    deleteItems.forEach((item) => updateItems.delete(item));

    // 한글 주석: delete에도 dc_rmk가 실려 가지만 백엔드는 PK로만 지우므로 분기하지 않는다.
    const form = searchRef.current.getFormdata();
    const rowsOf = (items: Set<string>) =>
      Array.from(items).map((item) => ({
        cd_firm: userInfo.cd_firm,
        cd_biz: getCurrentParent().cd_biz ?? form.cd_biz,
        cd_item: item,
        cd_emp: userInfo.cd_emp,
        dt_reg: todayform,
        dc_rmk: form.dc_rmk,
      }));

    return {
      insert: rowsOf(insertItems),
      update: rowsOf(updateItems),
      delete: rowsOf(deleteItems),
    };
  };

  // 한글 주석: reload=false는 onSearch가 저장 직후 스스로 조회할 때 쓴다(중복 조회 방지).
  const onSave = async (reload = true) => {
    try {
      const grid = getGridCurrent("grid2");
      grid.stopEditing();
      const requiredValuesCheckH = grid.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const allRows: RowData[] = [];
      grid.forEachNode((node) => {
        allRows.push(node.data);
      });
      const firstCdBiz = allRows[0]?.cd_biz;
      const hasMismatch = allRows.some((row) => row?.cd_biz !== firstCdBiz);

      if (hasMismatch) {
        await showDialog("alter", "사업장이 서로 다릅니다.");
        return false;
      }

      const rawChanges = grid.getChange();
      // 한글 주석: 0레벨(모품목) 행은 prd_boml 라인이 아니므로 어떤 변경도 저장하지 않는다.
      const notRoot = (row: RowData) => !isRootRow(row);
      const changesRow = {
        ...rawChanges,
        insert: (rawChanges.insert ?? []).filter(notRoot),
        update: (rawChanges.update ?? []).filter(notRoot),
        delete: (rawChanges.delete ?? []).filter(notRoot),
      };
      changesRow.modifiedRowcount =
        changesRow.insert.length +
        changesRow.update.length +
        changesRow.delete.length;
      if (changesRow.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      // 한글 주석: 트리 전용 필드는 prd_boml 컬럼이 아니므로 저장 payload에서 제거한다.
      const stripTreeKeys = (row: RowData) => {
        const {
          node,
          node_p,
          path,
          indent,
          sort_key,
          cd_matl_display,
          no_level,
          ...rest
        } = row;
        return rest;
      };
      const bomlSaveData = {
        ...changesRow,
        insert: (changesRow.insert ?? []).map(stripTreeKeys),
        update: (changesRow.update ?? []).map(stripTreeKeys),
        delete: (changesRow.delete ?? []).map(stripTreeKeys),
      };

      const saveData = {
        prd_bomh: buildBomhSaveData(changesRow),
        prd_boml: bomlSaveData,
      };
      const ok = await fnSave("SN_PRD_BAS_REG_004_SAVE_01", saveData);
      if (!ok) return false;

      grid.resetUpdate();
      // 한글 주석: 자품목 추가/삭제가 트리에 반영되도록 선택 노드를 유지한 채 재조회한다.
      if (reload) await loadBom();
      return true;
    } catch (error: unknown) {
      await showDialog(
        "error",
        getErrorMessage(error, "저장 중 오류가 발생했습니다.")
      );
      return false;
    }
  };

  const grids = {
    tree: {
      columns: columns.tree,
      dataSource: dataSources.tree,
      events: {
        onFocusedRowChanged,
        onRowDataReady: onRowDataReady,
      },
    },
    grid2: {
      columns: columns.grid2,
      dataSource: dataSources.grid2,
      etcProps: {
        rowSelection: "single" as const,
        title: "BOM정보",
        enableInitialSort: false,
      },
      events: {
        onBtnAddClick: onAddRow,
        onBtnDeleteClick: onDeleteRow,
        onCellValueChanged,
        onBeforeAddRow,
        onContextMenu: handleContextMenu,
      },
    },
    sheet1: {
      columns: columns.sheet1,
      dataSource: dataSources.sheet1,
    },
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="w-full flex flex-1 min-h-0 space-x-1.5">
        <SnResizeBar direction={"width"} id="panel1" orgSize="25%">
          <SnTree
            ref={gridRefs.current.tree}
            columnConfig={grids.tree.columns}
            rowData={grids.tree.dataSource}
            etcProps={{
              rowSelection: "single",
              title: "품목정보",
            }}
            header={
              <div>
                <SnButton
                  text={isTreeExpanded ? "전체닫기" : "전체전개"}
                  onClick={onTreeToggleExpand}
                />
              </div>
            }
            events={grids.tree.events}
            treeProps={{
              treeDataParentIdField: "node_p",
              treeIdKey: "node",
              treeOrderKey: "sort_key",
              groupDefaultExpanded: -1,
              autoGroupColumnDef,
              getRowId,
            }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"width"} id="panel2" orgSize="74.5%">
          <SnGrid
            ref={gridRefs.current.grid2}
            columnConfig={grids.grid2.columns}
            rowData={grids.grid2.dataSource}
            etcProps={grids.grid2.etcProps}
            events={grids.grid2.events}
            header={
              <div>
                <SnButton text="하위추가" onClick={onAddChildRow} />
              </div>
            }
          />
        </SnResizeBar>
      </div>
      <div style={{ display: "none" }}>
        <SnGrid
          ref={gridRefs.current.sheet1}
          columnConfig={grids.sheet1.columns}
          rowData={grids.sheet1.dataSource}
          etcProps={{ isEditable: false }}
        />
      </div>
    </div>
  );
});

export default SN_PRD_BAS_REG_044;
